import { z } from 'zod';
import {
  AgentCapabilityAnswerSchema,
  AgentCapabilityRequestSchema,
  AgentListenerBreadthSchema,
} from './agent-listener.types.js';
import { AgentStatusSchema, AgentTypeSchema } from './agent.types.js';
import { AgentPrAssociationRoleSchema } from './agent-association.types.js';
import { SandboxProcessSampleSchema } from './sandbox-metrics.types.js';
import { MonetaryAmountSchema } from './money.js';

import {
  TurnClaudeControlResponsePayloadSchema,
  type TurnClaudeControlResponsePayload,
  type TurnClaudeSystemPayload,
  type TurnClaudeUserPayload,
  type TurnClaudeAssistantPayload,
  type TurnClaudeResultPayload,
  type TurnClaudeErrorPayload,
  type TurnClaudeRateLimitEventPayload,
  type TurnClaudeControlRequestPayload,
  type UserMessage,
  type ClientControlRequest,
  type ClientControlResponse,
} from './claude-protocol.types.js';

export * from './claude-protocol.types.js';
import type {
  AxonEvent,
  AxonEventType,
  TurnStartedPayload,
  TurnMessageChunkPayload,
  TurnToolCallPayload,
  TurnToolCallUpdatePayload,
  TurnCompletedPayload,
  TurnCancelledPayload,
  TurnFailedPayload,
  TurnResumedPayload,
  BackgroundChangedPayload,
} from './event.types.js';

import { AttachmentSchema } from '@runloop/reflex-contract';

// --- Payload schemas ---

export const AgentStartedPayloadSchema = z.object({
  agentId: z.string(),
  agentType: AgentTypeSchema,
  name: z.string(),
  devboxId: z.string().nullable(),
});
export type AgentStartedPayload = z.infer<typeof AgentStartedPayloadSchema>;

export const AgentStoppedPayloadSchema = z.object({
  agentId: z.string(),
});
export type AgentStoppedPayload = z.infer<typeof AgentStoppedPayloadSchema>;

export const AgentInterruptedPayloadSchema = z.object({
  agentId: z.string(),
});
export type AgentInterruptedPayload = z.infer<typeof AgentInterruptedPayloadSchema>;

/**
 * Host-published agent error payload (via AgentRunnerService.transitionToTerminal).
 */
export const HostAgentErrorPayloadSchema = z.object({
  agentId: z.string(),
  title: z.string().optional(),
  error: z.string(),
  /**
   * Raw failure logs (e.g. the tail of a failed launch command or the
   * devbox's platform logs) shown in a collapsible block under the error
   * reason. Present when the server could capture output explaining the
   * failure; absent when no logs were available.
   */
  logs: z.string().optional(),
});
export type HostAgentErrorPayload = z.infer<typeof HostAgentErrorPayloadSchema>;

/**
 * Broker-published agent error payload (from Runloop broker via Axon).
 * Matches the `AgentErrorEvent` type from `@runloop/remote-agents-sdk`.
 */
export const BrokerAgentErrorPayloadSchema = z.object({
  devboxId: z.string().optional(),
  errorType: z.string().optional(),
  message: z.string().optional(),
});
export type BrokerAgentErrorPayload = z.infer<typeof BrokerAgentErrorPayloadSchema>;

/**
 * Union of host-published and broker-published agent error payloads.
 * - Host-published: `{ agentId, error }` — emitted by the server on startup failures.
 * - Broker-published: `{ devboxId?, errorType?, message? }` — emitted by the Runloop broker.
 */
export const AgentErrorPayloadSchema = z.union([
  HostAgentErrorPayloadSchema,
  BrokerAgentErrorPayloadSchema,
]);
export type AgentErrorPayload = z.infer<typeof AgentErrorPayloadSchema>;

/**
 * Broker-published log line from the agent process. The broker streams the
 * sandbox's stdout/stderr as `agent.log` events; the chat renders stdout/stderr
 * lines as muted log text.
 */
export const AgentLogPayloadSchema = z.object({
  log_type: z.string().optional(),
  message: z.string().optional(),
});
export type AgentLogPayload = z.infer<typeof AgentLogPayloadSchema>;

export const MessagePayloadSchema = z.object({
  agentId: z.string(),
  message: z.string(),
  role: z.enum(['user', 'agent']).optional(),
  attachments: z.array(AttachmentSchema).optional(),
  userId: z.string().optional(),
  userName: z.string().optional(),
  userAvatarUrl: z.string().optional(),
});
export type MessagePayload = z.infer<typeof MessagePayloadSchema>;

// --- Agent protocol payload schemas ---

export const PlanStepStatusSchema = z.enum(['pending', 'in_progress', 'completed']);
export type PlanStepStatus = z.infer<typeof PlanStepStatusSchema>;

export const PlanStepSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: PlanStepStatusSchema,
});
export type PlanStep = z.infer<typeof PlanStepSchema>;

export const AgentPlanPayloadSchema = z.object({
  agentId: z.string(),
  plan: z.string(),
  steps: z.array(PlanStepSchema),
});
export type AgentPlanPayload = z.infer<typeof AgentPlanPayloadSchema>;

export const AgentProgressPayloadSchema = z.object({
  agentId: z.string(),
  stepId: z.string(),
  status: PlanStepStatusSchema,
  detail: z.string().optional(),
});
export type AgentProgressPayload = z.infer<typeof AgentProgressPayloadSchema>;

export const AgentNeedInputPayloadSchema = z.object({
  agentId: z.string(),
  question: z.string(),
});
export type AgentNeedInputPayload = z.infer<typeof AgentNeedInputPayloadSchema>;

export const AgentCompletePayloadSchema = z.object({
  agentId: z.string(),
  summary: z.string(),
});
export type AgentCompletePayload = z.infer<typeof AgentCompletePayloadSchema>;

export const AgentNamePayloadSchema = z.object({
  agentId: z.string(),
  name: z.string(),
});
export type AgentNamePayload = z.infer<typeof AgentNamePayloadSchema>;

export const AgentPRCreatedPayloadSchema = z.object({
  agentId: z.string(),
  url: z.string(),
  number: z.number(),
  title: z.string(),
  repo: z.string(),
  branch: z.string(),
  /**
   * The agent's relationship to the PR: it opened it (`creator`) or it
   * reviewed someone else's (`reviewer`). Absent — an older on-box CLI —
   * means `creator`, which was the only behavior before roles existed.
   */
  role: AgentPrAssociationRoleSchema.optional(),
  /**
   * Whether the PR was still a draft when it was registered. Absent — an older
   * on-box CLI, or a `gh` that could not answer — leaves the association's
   * state unrecorded, which reads as `open`.
   */
  draft: z.boolean().optional(),
});
export type AgentPRCreatedPayload = z.infer<typeof AgentPRCreatedPayloadSchema>;

export const AgentPRMergedPayloadSchema = z.object({
  agentId: z.string(),
  url: z.string(),
  number: z.number(),
  title: z.string(),
  repo: z.string(),
});
export type AgentPRMergedPayload = z.infer<typeof AgentPRMergedPayloadSchema>;

export const AgentPRClosedPayloadSchema = z.object({
  agentId: z.string(),
  url: z.string(),
  number: z.number(),
  title: z.string(),
  repo: z.string(),
});
export type AgentPRClosedPayload = z.infer<typeof AgentPRClosedPayloadSchema>;

export const AgentPRChecksFailedPayloadSchema = z.object({
  agentId: z.string(),
  url: z.string(),
  number: z.number(),
  title: z.string(),
  repo: z.string(),
  checkName: z.string(),
  conclusion: z.string(),
  detailsUrl: z.string().optional(),
});
export type AgentPRChecksFailedPayload = z.infer<typeof AgentPRChecksFailedPayloadSchema>;

export const AgentPRReviewStateSchema = z.enum(['approved', 'changes_requested', 'commented']);
export type AgentPRReviewState = z.infer<typeof AgentPRReviewStateSchema>;

export const AgentPRReviewPayloadSchema = z.object({
  agentId: z.string(),
  url: z.string(),
  number: z.number(),
  title: z.string(),
  repo: z.string(),
  state: AgentPRReviewStateSchema,
  reviewer: z.string().optional(),
});
export type AgentPRReviewPayload = z.infer<typeof AgentPRReviewPayloadSchema>;

/**
 * Core setup step identifiers emitted by the host for every agent.
 *
 * `init` publishes the full, ordered list of expected steps up front (before
 * the devbox boots). `init.append` appends additional steps at runtime — this
 * is used by `monitorLaunch` when it discovers services that the agent
 * registered from inside the devbox (via `reflex-service register`).
 *
 * Plugins contribute additional step ids via `DevboxSetupHook.execute`,
 * `DevboxService`, and `DevboxLaunchCommand`; those are auto-promoted into the
 * `init` payload and are not listed here.
 */
export const CORE_SETUP_STEPS = [
  'init',
  'init.append',
  'creating_devbox',
  'github-setup',
  'launching_agent',
] as const;

/**
 * Step id for the combined run-once GitHub-setup step (git auth + repo clone).
 * Shared so the host (declaring the expected step), the manifest builder
 * (writing the on-box script + status files), and the monitor (labelling the
 * discovered step) all agree on the same id.
 */
export const GITHUB_SETUP_STEP_ID = 'github-setup';

/**
 * Step ids for the run-once credential-mount steps (Claude Max / Codex
 * subscription). Shared so the host (wrapping the chmod as a `once` launch
 * command) and the monitor (labelling the discovered step) agree on the id.
 */
export const CLAUDE_MAX_MOUNT_STEP_ID = 'claude-max-mount';
export const CODEX_SUBSCRIPTION_MOUNT_STEP_ID = 'codex-subscription-mount';

export const AgentSetupStepSchema = z.string();
export type AgentSetupStep = z.infer<typeof AgentSetupStepSchema>;

export const AgentSetupPayloadSchema = z.object({
  agentId: z.string(),
  step: AgentSetupStepSchema,
  detail: z.string().optional(),
  /**
   * Wall-clock duration of the step, in milliseconds. Present on the
   * resolving event for steps we time: provisioning (server-side
   * create→running clock) and on-box subtasks (from each step's
   * `result.json`). Absent for steps without timing.
   */
  durationMs: z.number().optional(),
  /**
   * For `creating_devbox`: our-owned initializing span (blocking launch
   * commands run inside Runloop's `initializing` phase) in milliseconds,
   * separate from the Runloop-owned provisioning span carried in
   * `durationMs`. Absent for every other step.
   */
  initializingMs: z.number().optional(),
  /**
   * Whether this event marks the step as terminally resolved. A monitored
   * service emits non-terminal statuses (`installing`, `starting`,
   * `waiting_ready`) with `terminal: false` while still coming up, then a
   * final event with `terminal: true` on `ready`/failure. Absent means
   * terminal (steps emitted once at completion, e.g. the blocking lifecycle
   * steps). The UI must not mark a step done until it sees a terminal event.
   */
  terminal: z.boolean().optional(),
});
export type AgentSetupPayload = z.infer<typeof AgentSetupPayloadSchema>;

/**
 * Structured `detail` payload for setup steps that register a list of named
 * sub-items (today: MCP server registration). Encoded as JSON inside the
 * `detail` string so it travels through the existing `AgentSetupPayload`
 * schema unchanged. The UI renders one sub-row per server with its own
 * status icon and click-to-expand log section.
 */
export const McpServerSetupStatusSchema = z.object({
  name: z.string(),
  /** `ok`/`failed` report devbox-side registration; `skipped` means dropped before provisioning. */
  status: z.enum(['ok', 'failed', 'skipped']),
  /** User-actionable explanation for a `skipped` server. */
  reason: z.string().optional(),
});
export type McpServerSetupStatus = z.infer<typeof McpServerSetupStatusSchema>;

export const McpServerSetupDetailSchema = z.object({
  kind: z.literal('mcp-servers'),
  /** Human-readable summary, e.g. `3 registered` or `2 registered, 1 failed: slack`. */
  summary: z.string(),
  servers: z.array(McpServerSetupStatusSchema),
});
export type McpServerSetupDetail = z.infer<typeof McpServerSetupDetailSchema>;

/**
 * Encode a {@link McpServerSetupDetail} as the JSON string carried by
 * `AgentSetupPayload.detail`. Centralised so server hooks and the UI
 * cannot drift on the wire format.
 */
export function encodeMcpServerSetupDetail(detail: McpServerSetupDetail): string {
  return JSON.stringify(detail);
}

/**
 * Best-effort decode of a setup step's `detail` string into a
 * {@link McpServerSetupDetail}. Returns `null` for plain-text legacy
 * details or anything that fails schema validation, so callers can fall
 * back to displaying the raw string.
 */
export function decodeMcpServerSetupDetail(
  detail: string | undefined,
): McpServerSetupDetail | null {
  if (!detail) return null;
  if (detail[0] !== '{') return null;
  try {
    const parsed: unknown = JSON.parse(detail);
    const result = McpServerSetupDetailSchema.safeParse(parsed);
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

export const AgentToolUsePayloadSchema = z.object({
  agentId: z.string(),
  tool: z.string(),
  path: z.string().optional(),
  detail: z.string().optional(),
});
export type AgentToolUsePayload = z.infer<typeof AgentToolUsePayloadSchema>;

// --- AskUserQuestion structured detail ---

export const AgentDevServerPayloadSchema = z.object({
  agentId: z.string(),
  port: z.number(),
  name: z.string().optional(),
});
export type AgentDevServerPayload = z.infer<typeof AgentDevServerPayloadSchema>;

/**
 * Forwarded from a `warning` message on the bridge control channel when
 * its on-box sustained-high detection trips. The bridge does the detection
 * (the agent does), the server just relays the warning to the agent's
 * axon stream as `agent.performance_warning`. Carries `limit` (the
 * discovered effective cgroup limit) and `windowSeconds` so the chat
 * bubble can read "CPU sustained above 85% for 60s (2 cores)" without
 * having to look the box's capacity up separately.
 */
export const AgentPerformanceWarningPayloadSchema = z.object({
  agentId: z.string(),
  metric: z.enum(['cpu', 'memory']),
  value: z.number(),
  threshold: z.number(),
  limit: z.number(),
  windowSeconds: z.number().positive(),
  message: z.string(),
  /**
   * The metric's value across the detection window (oldest -> newest, percent
   * for cpu/memory), forwarded from the bridge so the chat warning can render
   * a static mini-graph of the offending period. Empty when an older bridge
   * didn't supply it.
   */
  series: z.array(z.number()).default([]),
  /**
   * Top processes (by the offending metric) captured when the warning tripped,
   * forwarded from the bridge so the chat warning can show who was using the
   * resource. Empty when an older bridge didn't supply it.
   */
  processes: z.array(SandboxProcessSampleSchema).default([]),
});
export type AgentPerformanceWarningPayload = z.infer<typeof AgentPerformanceWarningPayloadSchema>;

/**
 * Forwarded from a `notify { kind: 'daemon' }` message on the bridge
 * control channel. The on-box CLI / daemon skill POSTs to the bridge's
 * localhost IPC endpoint when it starts a long-running service, the
 * bridge relays it as `notify`, and the server publishes
 * `agent.daemon_started` so the chat surface can render a styled bubble
 * with a clickable "Open in browser" button at the agent's tunnel URL.
 */
export const AgentDaemonStartedPayloadSchema = z.object({
  agentId: z.string(),
  name: z.string(),
  port: z.number().int().positive().optional(),
  /** Pre-built tunnel URL (server fills this in from `agent.tunnelKey + port`). */
  url: z.string().optional(),
  info: z.string().optional(),
});
export type AgentDaemonStartedPayload = z.infer<typeof AgentDaemonStartedPayloadSchema>;

/**
 * The card an agent's capability request renders in its thread. Everything
 * a human needs to answer is here, breadth first: the same verb ("watch
 * PRs") can mean one repo or the whole organization, and the headline says
 * which. Answered through `POST /agents/:id/capability-requests/:requestId/decision`.
 */
export const AgentCapabilityRequestedPayloadSchema = z.object({
  agentId: z.string(),
  /** The pending listener (or, later, action request) this card decides. */
  requestId: z.string(),
  requestKind: z.enum(['listener', 'action']),
  /** What is being asked for, as the matcher the grant will be written for. */
  request: AgentCapabilityRequestSchema,
  /** One line, breadth first: "Watch every PR in acme/reflex". */
  headline: z.string(),
  breadth: AgentListenerBreadthSchema.nullable(),
  /** The stream in plain words, e.g. "pull request reviews, when submitted". */
  stream: z.string().nullable(),
  /** The filter as one sentence per clause, for a watch. */
  filterLines: z.array(z.string()),
  /**
   * How many events the org received from this source in the last 7 days:
   * an upper bound on how often the watch would have fired. Null when the
   * inbox cannot say.
   */
  sourceEventsLastWeek: z.number().int().nullable(),
  leaseMs: z.number().int().nullable(),
  maxFires: z.number().int().nullable(),
  interrupts: z.boolean(),
  /** The agent's own words, verbatim. */
  reason: z.string(),
  requestedByUserId: z.string().nullable(),
  /** A second request folded into the same card (an interrupt on a signal). */
  alsoRequests: z.array(AgentCapabilityRequestSchema),
});
export type AgentCapabilityRequestedPayload = z.infer<typeof AgentCapabilityRequestedPayloadSchema>;

export const AgentCapabilityResolvedPayloadSchema = z.object({
  agentId: z.string(),
  requestId: z.string(),
  answer: AgentCapabilityAnswerSchema,
  decision: z.enum(['allow', 'deny']),
  decidedByUserId: z.string().nullable(),
});
export type AgentCapabilityResolvedPayload = z.infer<typeof AgentCapabilityResolvedPayloadSchema>;

/**
 * Announcement envelope for a work assessment, published by
 * `@reflex/plugin-work-assessment` when its on-box subcommand posts one
 * through the sandbox bridge.
 *
 * Like `flow.*` and `agent.pr_*`, the event type lives in the host enum
 * because `AxonEventTypeSchema` is closed — but the shape here is only what a
 * *different* package needs to announce the assessment without depending on
 * the plugin: `plugin-chat` renders the card from these fields alone.
 * `document` is the assessment itself and is deliberately `unknown` — its
 * schema is owned by the plugin, which parses it at its render boundary — so
 * the document can evolve with no host release.
 *
 * There is no assessment id: the axon event id is the assessment's identity,
 * which is what the chat card deep-links the tab to.
 */
export const AgentWorkAssessmentPayloadSchema = z.object({
  agentId: z.string(),
  /** Headline ("Route-context RBAC framework"). */
  title: z.string(),
  /** One-or-two sentence abstract shown on the chat card. */
  summary: z.string().optional(),
  /**
   * What was assessed. `kind` stays a plain string rather than an enum: it is
   * chosen by the on-box skill, and a newer skill naming a kind this host has
   * never heard of must still render as an assessment. Consumers narrow it
   * for iconography and fall back to a generic label.
   */
  target: z.object({ kind: z.string(), label: z.string(), url: z.string().optional() }).optional(),
  /** Plugin-owned assessment document. See the doc comment above. */
  document: z.unknown().optional(),
});
export type AgentWorkAssessmentPayload = z.infer<typeof AgentWorkAssessmentPayloadSchema>;

// --- Claude Max credential refresh system events ---

/**
 * System event published when the on-box flex credential refresh step receives
 * a Claude Max access token from reflex. `expiresAt` is the new token's expiry
 * (epoch ms). Origin `SYSTEM_EVENT`; published on the agent's stream so the
 * refresh shows up in the timeline.
 */
export const AgentCredentialsRefreshedPayloadSchema = z.object({
  agentId: z.string(),
  provider: z.literal('claude-max'),
  // Epoch ms; matches `ClaudeOAuthInnerSchema.expiresAt` so garbage/negative
  // epochs are rejected at the wire boundary.
  expiresAt: z.number().int().positive(),
});
export type AgentCredentialsRefreshedPayload = z.infer<
  typeof AgentCredentialsRefreshedPayloadSchema
>;

/**
 * System event published when reflex cannot refresh a running box's Claude Max
 * credential. `missing` = no stored credential resolved for the agent;
 * `needs-reconnect` = the stored refresh token is revoked/expired and the user
 * must re-auth. Origin `SYSTEM_EVENT`.
 */
export const AgentCredentialsRefreshFailedPayloadSchema = z.object({
  agentId: z.string(),
  provider: z.literal('claude-max'),
  reason: z.enum(['missing', 'needs-reconnect']),
});
export type AgentCredentialsRefreshFailedPayload = z.infer<
  typeof AgentCredentialsRefreshFailedPayloadSchema
>;

// --- Agent subagent + compact payload schemas ---

export const AgentSubagentPayloadSchema = z.object({
  agentId: z.string(),
  subagentType: z.string(),
  description: z.string(),
  status: z.enum(['started', 'completed']),
});
export type AgentSubagentPayload = z.infer<typeof AgentSubagentPayloadSchema>;

export const AgentCompactPayloadSchema = z.object({
  agentId: z.string(),
  reason: z.enum(['auto', 'manual']),
});
export type AgentCompactPayload = z.infer<typeof AgentCompactPayloadSchema>;

export const AgentStatusChangePayloadSchema = z.object({
  agentId: z.string(),
  fromStatus: AgentStatusSchema,
  toStatus: AgentStatusSchema,
});
export type AgentStatusChangePayload = z.infer<typeof AgentStatusChangePayloadSchema>;

/**
 * Announces a live agent's model switch, from the composer chip or a typed
 * `/model`. The announcement, not the record: `agents.model` is written first.
 */
export const AgentRuntimeSettingsChangedPayloadSchema = z.object({
  agentId: z.string(),
  fromModel: z.string().nullable(),
  toModel: z.string(),
  /**
   * Stream sequence of the harness instruction this announces. Announcements
   * of racing switches can arrive out of post order; the highest sequence
   * names the model in effect.
   */
  sequence: z.number().int().nonnegative(),
  /**
   * `api` for the composer chip, `command` for a typed `/model`. A `command`
   * event without `command` text is an older Codex announcement, which the
   * transcript already shows as the command's result.
   */
  source: z.enum(['api', 'command']),
  /** The typed `/model` text. */
  command: z.string().optional(),
  /** The user who asked for it; absent for server-authored changes. */
  userId: z.string().optional(),
});
export type AgentRuntimeSettingsChangedPayload = z.infer<
  typeof AgentRuntimeSettingsChangedPayloadSchema
>;

/**
 * Sticky Codex turn overrides accumulated from slash commands. Each
 * `codex.command.result` event carries the full snapshot after its command,
 * so replaying a stream reduces to reading the newest event.
 */
export const CodexTurnOverridesSchema = z.object({
  model: z.string().optional(),
  effort: z.string().optional(),
  serviceTier: z.string().nullable().optional(),
  planMode: z.boolean().optional(),
});
export type CodexTurnOverrides = z.infer<typeof CodexTurnOverridesSchema>;

export const CodexCommandResultPayloadSchema = z.object({
  command: z.string(),
  status: z.enum(['ok', 'error']),
  message: z.string(),
  overrides: CodexTurnOverridesSchema,
});
export type CodexCommandResultPayload = z.infer<typeof CodexCommandResultPayloadSchema>;

// --- Devbox lifecycle payload schemas (system events from the Axon broker) ---

export const DevboxLifecyclePayloadSchema = z.object({
  devboxId: z.string(),
  kind: z.enum(['running', 'suspended', 'shutdown', 'failed']),
  reason: z.string().optional(),
});
export type DevboxLifecyclePayload = z.infer<typeof DevboxLifecyclePayloadSchema>;

/**
 * Published when the host cleans up a completed agent's devbox because the
 * organization's sandbox retention window elapsed. `message` is a
 * user-facing explanation rendered on the agent stream; `retentionDays` is
 * the policy that was in effect so the UI can be specific about why the
 * sandbox is gone and can no longer be used.
 */
export const DevboxCleanupPayloadSchema = z.object({
  agentId: z.string(),
  devboxId: z.string(),
  retentionDays: z.number().int().positive(),
  message: z.string(),
});
export type DevboxCleanupPayload = z.infer<typeof DevboxCleanupPayloadSchema>;

// --- Agent source payload schemas (core only — plugin-specific payloads live in their plugins) ---

export const AgentSourceDashboardPayloadSchema = z.object({
  userId: z.string(),
  userLogin: z.string(),
});
export type AgentSourceDashboardPayload = z.infer<typeof AgentSourceDashboardPayloadSchema>;

/**
 * Source banner published once on an agent's stream when the agent is launched
 * by a flow run. Lets the chat UI show "Started from automation: <name>" with a
 * deep-link back to the originating node on the flow run page.
 */
export const AgentSourceFlowPayloadSchema = z.object({
  agentId: z.string(),
  flowId: z.string(),
  flowName: z.string(),
  flowRunId: z.string(),
  nodeId: z.string(),
  nodeName: z.string(),
  nodeType: z.enum(['agent', 'agent_task']),
  prompt: z.string().optional(),
});
export type AgentSourceFlowPayload = z.infer<typeof AgentSourceFlowPayloadSchema>;

/**
 * Published on a flow run's stream when an `agent_task` turn failed with a
 * provider-classified transient error and the engine is re-prompting the same
 * agent session instead of failing the node. The node stays `running`; a
 * terminal `flow.node_completed`/`flow.node_error` still follows.
 */
export const FlowNodeRetryingPayloadSchema = z.object({
  runId: z.string(),
  nodeId: z.string(),
  nodeName: z.string(),
  /** 1-based retry attempt about to be made. */
  attempt: z.number(),
  /** Total retry attempts allowed after the first try. */
  maxAttempts: z.number(),
  /** The transient error that triggered the retry. */
  error: z.string(),
});
export type FlowNodeRetryingPayload = z.infer<typeof FlowNodeRetryingPayloadSchema>;

/**
 * Result card published on the agent's stream when the agent's output is
 * handed back to the flow engine. Carries the final summary plus any
 * structured output so the chat surface can render the same data the flow
 * run page will consume.
 */
export const ReflexResultPayloadSchema = z.object({
  agentId: z.string(),
  flowId: z.string(),
  flowName: z.string(),
  flowRunId: z.string(),
  nodeId: z.string(),
  nodeName: z.string(),
  nodeType: z.enum(['agent', 'agent_task']),
  summary: z.string().nullable(),
  structuredOutput: z.record(z.string(), z.unknown()).nullable().optional(),
});
export type ReflexResultPayload = z.infer<typeof ReflexResultPayloadSchema>;

// Broker control payloads
export const BrokerControlInterruptPayloadSchema = z.object({
  agentId: z.string(),
});
export type BrokerControlInterruptPayload = z.infer<typeof BrokerControlInterruptPayloadSchema>;

export const BrokerControlCancelPayloadSchema = z.object({
  agentId: z.string(),
});
export type BrokerControlCancelPayload = z.infer<typeof BrokerControlCancelPayloadSchema>;

/**
 * broker.claude.control payload — a Claude CLI control response forwarded
 * from the UI through the broker to the devbox. Same shape as
 * TurnClaudeControlResponsePayload (sent raw, not wrapped in { agentId }).
 */
export const BrokerClaudeControlPayloadSchema = TurnClaudeControlResponsePayloadSchema;
export type BrokerClaudeControlPayload = TurnClaudeControlResponsePayload;

/**
 * broker.acp.control payload — an ACP control response forwarded from the
 * UI through the broker. The concrete shape depends on the ACP server; typed
 * as unknown until a formal schema is defined.
 */
export const BrokerAcpControlPayloadSchema = z.unknown();
export type BrokerAcpControlPayload = unknown;

/** Codex's `thread/settings/update` request, posted by the host to switch a run's model. */
export const CodexThreadSettingsUpdatePayloadSchema = z.object({
  id: z.string(),
  method: z.literal('thread/settings/update'),
  params: z.object({ threadId: z.string(), model: z.string().min(1) }),
});
export type CodexThreadSettingsUpdatePayload = z.infer<
  typeof CodexThreadSettingsUpdatePayloadSchema
>;

// --- ACP (Agent Client Protocol) payload types ---

/** ContentBlock types used in ACP messages */
export const AcpContentBlockSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('text'), text: z.string() }),
  z.object({ type: z.literal('image'), mimeType: z.string(), data: z.string() }),
  z.object({ type: z.literal('imageUrl'), url: z.string() }),
  z.object({ type: z.literal('resource'), resource: z.unknown() }),
  z.object({ type: z.literal('resourceUrl'), url: z.string() }),
]);
export type AcpContentBlock = z.infer<typeof AcpContentBlockSchema>;

export const AcpContentChunkSchema = z.object({
  content: AcpContentBlockSchema,
  _meta: z.record(z.string(), z.unknown()).optional(),
});
export type AcpContentChunk = z.infer<typeof AcpContentChunkSchema>;

/**
 * ACP PromptRequest - sent via session/prompt event to start a turn
 */
export const AcpPromptRequestSchema = z.object({
  sessionId: z.string(),
  prompt: z.array(AcpContentBlockSchema),
});
export type AcpPromptRequest = z.infer<typeof AcpPromptRequestSchema>;

/**
 * ACP StopReason - why a prompt turn ended.
 */
export const AcpStopReasonSchema = z.enum([
  'end_turn',
  'max_tokens',
  'max_turn_requests',
  'refusal',
  'cancelled',
]);
export type AcpStopReason = z.infer<typeof AcpStopReasonSchema>;

/**
 * ACP PromptResponse - the agent's reply to session/prompt, echoed on the
 * stream as an AGENT_EVENT under the same `session/prompt` event type. This
 * is the protocol-native turn-end signal; `stopReason` distinguishes it
 * from the request echo, which carries `prompt` instead.
 */
export const AcpPromptResponseSchema = z.object({
  stopReason: AcpStopReasonSchema,
  _meta: z.record(z.string(), z.unknown()).optional(),
});
export type AcpPromptResponse = z.infer<typeof AcpPromptResponseSchema>;

/**
 * ACP CancelNotification - sent via session/cancel to interrupt a turn
 */
export const AcpCancelNotificationSchema = z.object({
  sessionId: z.string(),
});
export type AcpCancelNotification = z.infer<typeof AcpCancelNotificationSchema>;

/**
 * ACP InitializeRequest - sent via 'initialize' to negotiate protocol version and capabilities
 */
export const AcpInitializeRequestSchema = z.object({
  protocolVersion: z.number(),
  clientCapabilities: z
    .object({
      'fs.readTextFile': z.boolean().optional(),
      'fs.writeTextFile': z.boolean().optional(),
      terminal: z.boolean().optional(),
    })
    .optional(),
  clientInfo: z
    .object({
      name: z.string(),
      title: z.string().optional(),
      version: z.string().optional(),
    })
    .optional(),
});
export type AcpInitializeRequest = z.infer<typeof AcpInitializeRequestSchema>;

/**
 * ACP NewSessionRequest - sent via 'session/new' to create a new session
 */
export const AcpNewSessionRequestSchema = z.object({
  cwd: z.string(),
  mcpServers: z
    .array(
      z.object({
        name: z.string(),
        command: z.string(),
        args: z.array(z.string()).optional(),
        env: z.array(z.string()).optional(),
      }),
    )
    .optional(),
});
export type AcpNewSessionRequest = z.infer<typeof AcpNewSessionRequestSchema>;

/**
 * ACP InitializeResponse - response from 'initialize' with negotiated capabilities
 */
export const AcpInitializeResponseSchema = z.object({
  protocolVersion: z.number(),
  agentCapabilities: z
    .object({
      loadSession: z.boolean().optional(),
      promptCapabilities: z
        .object({
          image: z.boolean().optional(),
          audio: z.boolean().optional(),
          embeddedContext: z.boolean().optional(),
        })
        .optional(),
      mcpCapabilities: z
        .object({
          http: z.boolean().optional(),
          sse: z.boolean().optional(),
        })
        .optional(),
    })
    .optional(),
  agentInfo: z
    .object({
      name: z.string(),
      title: z.string().optional(),
      version: z.string().optional(),
    })
    .optional(),
  authMethods: z.array(z.string()).optional(),
});
export type AcpInitializeResponse = z.infer<typeof AcpInitializeResponseSchema>;

/**
 * ACP NewSessionResponse - response from 'session/new' with the sessionId
 */
export const AcpNewSessionResponseSchema = z.object({
  sessionId: z.string(),
  modes: z
    .object({
      currentModeId: z.string().optional(),
      availableModes: z
        .array(
          z.object({
            id: z.string(),
            name: z.string(),
            description: z.string().optional(),
          }),
        )
        .optional(),
    })
    .optional(),
  _meta: z.record(z.string(), z.unknown()).optional(),
});
export type AcpNewSessionResponse = z.infer<typeof AcpNewSessionResponseSchema>;

export const AcpUserMessageChunkPayloadSchema = z.object({
  sessionId: z.string(),
  update: z.object({
    sessionUpdate: z.literal('user_message_chunk'),
    content: AcpContentBlockSchema,
  }),
  _meta: z.record(z.string(), z.unknown()).optional(),
});
export type AcpUserMessageChunkPayload = z.infer<typeof AcpUserMessageChunkPayloadSchema>;

export const AcpAgentMessageChunkPayloadSchema = z.object({
  sessionId: z.string(),
  update: z.object({
    sessionUpdate: z.literal('agent_message_chunk'),
    content: AcpContentBlockSchema,
  }),
  _meta: z.record(z.string(), z.unknown()).optional(),
});
export type AcpAgentMessageChunkPayload = z.infer<typeof AcpAgentMessageChunkPayloadSchema>;

export const AcpAgentThoughtChunkPayloadSchema = z.object({
  sessionId: z.string(),
  update: z.object({
    sessionUpdate: z.literal('agent_thought_chunk'),
    content: AcpContentBlockSchema,
  }),
  _meta: z.record(z.string(), z.unknown()).optional(),
});
export type AcpAgentThoughtChunkPayload = z.infer<typeof AcpAgentThoughtChunkPayloadSchema>;

export const AcpToolKindSchema = z.enum([
  'read',
  'edit',
  'delete',
  'move',
  'search',
  'execute',
  'think',
  'fetch',
  'other',
]);
export type AcpToolKind = z.infer<typeof AcpToolKindSchema>;

export const AcpToolCallStatusSchema = z.enum(['pending', 'in_progress', 'completed', 'failed']);
export type AcpToolCallStatus = z.infer<typeof AcpToolCallStatusSchema>;

export const AcpToolCallPayloadSchema = z.object({
  sessionId: z.string(),
  update: z.object({
    sessionUpdate: z.literal('tool_call'),
    toolCallId: z.string(),
    kind: AcpToolKindSchema.optional(),
    title: z.string().optional(),
    status: AcpToolCallStatusSchema.optional(),
    content: z.array(z.unknown()).optional(),
    rawInput: z.unknown().optional(),
    rawOutput: z.unknown().optional(),
  }),
  _meta: z.record(z.string(), z.unknown()).optional(),
});
export type AcpToolCallPayload = z.infer<typeof AcpToolCallPayloadSchema>;

export const AcpToolCallUpdatePayloadSchema = z.object({
  sessionId: z.string(),
  update: z.object({
    sessionUpdate: z.literal('tool_call_update'),
    toolCallId: z.string(),
    kind: AcpToolKindSchema.optional(),
    title: z.string().optional(),
    status: AcpToolCallStatusSchema.optional(),
    content: z.array(z.unknown()).optional(),
    rawInput: z.unknown().optional(),
    rawOutput: z.unknown().optional(),
    fields: z
      .object({
        kind: AcpToolKindSchema.optional(),
        title: z.string().optional(),
        status: AcpToolCallStatusSchema.optional(),
        content: z.array(z.unknown()).optional(),
      })
      .optional(),
  }),
  _meta: z.record(z.string(), z.unknown()).optional(),
});
export type AcpToolCallUpdatePayload = z.infer<typeof AcpToolCallUpdatePayloadSchema>;

export const AcpPlanStepStatusSchema = z.enum(['pending', 'in_progress', 'completed', 'failed']);
export type AcpPlanStepStatus = z.infer<typeof AcpPlanStepStatusSchema>;

export const AcpPlanStepSchema: z.ZodType<{
  id: string;
  title: string;
  status: AcpPlanStepStatus;
  steps?: AcpPlanStep[];
}> = z.lazy(() =>
  z.object({
    id: z.string(),
    title: z.string(),
    status: AcpPlanStepStatusSchema,
    steps: z.array(AcpPlanStepSchema).optional(),
  }),
);
export type AcpPlanStep = z.infer<typeof AcpPlanStepSchema>;

export const AcpPlanPayloadSchema = z.object({
  sessionId: z.string(),
  update: z.object({
    sessionUpdate: z.literal('plan'),
    steps: z.array(AcpPlanStepSchema),
  }),
  _meta: z.record(z.string(), z.unknown()).optional(),
});
export type AcpPlanPayload = z.infer<typeof AcpPlanPayloadSchema>;

export const AcpAvailableCommandsUpdatePayloadSchema = z.object({
  sessionId: z.string(),
  update: z.object({
    sessionUpdate: z.literal('available_commands_update'),
    availableCommands: z.array(z.unknown()),
  }),
  _meta: z.record(z.string(), z.unknown()).optional(),
});
export type AcpAvailableCommandsUpdatePayload = z.infer<
  typeof AcpAvailableCommandsUpdatePayloadSchema
>;

export const AcpCurrentModeUpdatePayloadSchema = z.object({
  sessionId: z.string(),
  update: z.object({
    sessionUpdate: z.literal('current_mode_update'),
    currentModeId: z.string(),
  }),
  _meta: z.record(z.string(), z.unknown()).optional(),
});
export type AcpCurrentModeUpdatePayload = z.infer<typeof AcpCurrentModeUpdatePayloadSchema>;

export const AcpConfigOptionUpdatePayloadSchema = z.object({
  sessionId: z.string(),
  update: z.object({
    sessionUpdate: z.literal('config_option_update'),
    configOptions: z.array(z.unknown()),
  }),
  _meta: z.record(z.string(), z.unknown()).optional(),
});
export type AcpConfigOptionUpdatePayload = z.infer<typeof AcpConfigOptionUpdatePayloadSchema>;

export const AcpSessionInfoUpdatePayloadSchema = z.object({
  sessionId: z.string(),
  update: z.object({
    sessionUpdate: z.literal('session_info_update'),
    title: z.string().nullable().optional(),
    updatedAt: z.string().optional(),
  }),
  _meta: z.record(z.string(), z.unknown()).optional(),
});
export type AcpSessionInfoUpdatePayload = z.infer<typeof AcpSessionInfoUpdatePayloadSchema>;

/**
 * ACP `usage_update` session update: token/cost statistics for the current
 * session. `cost` is the agent's **cumulative** session cost (ACP `Cost`,
 * with an ISO 4217 currency); `used`/`size` describe context occupancy, not
 * billable token consumption. This is the only usage signal ACP agents
 * (OpenCode, Cursor) publish, and it feeds the costs plugin's
 * cumulative→delta recorder via `extractLatestAcpCost`.
 */
export const AcpUsageUpdateSchema = z.object({
  sessionUpdate: z.literal('usage_update'),
  /** Tokens currently in context. */
  used: z.number(),
  /** Total context window size in tokens. */
  size: z.number(),
  /**
   * Cumulative session cost; absent when the agent does not track cost.
   * Read it through `usdFromMonetaryAmount` — the currency is whatever the
   * agent reports, not necessarily USD.
   */
  cost: MonetaryAmountSchema.nullable().optional(),
});
export type AcpUsageUpdate = z.infer<typeof AcpUsageUpdateSchema>;

/** A {@link AcpUsageUpdateSchema} as it arrives on the stream. */
export const AcpUsageUpdatePayloadSchema = z.object({
  sessionId: z.string(),
  update: AcpUsageUpdateSchema,
  _meta: z.record(z.string(), z.unknown()).optional(),
});
export type AcpUsageUpdatePayload = z.infer<typeof AcpUsageUpdatePayloadSchema>;

// --- Event map: event type string -> payload type ---

export interface AxonEventMap {
  'agent.started': AgentStartedPayload;
  'agent.stopped': AgentStoppedPayload;
  'agent.killed': AgentStoppedPayload;
  'agent.interrupted': AgentInterruptedPayload;
  'agent.error': AgentErrorPayload;
  'agent.log': AgentLogPayload;
  'agent.name': AgentNamePayload;
  'agent.plan': AgentPlanPayload;
  'agent.progress': AgentProgressPayload;
  'agent.need_input': AgentNeedInputPayload;
  'agent.complete': AgentCompletePayload;
  'agent.pr_created': AgentPRCreatedPayload;
  'agent.pr_merged': AgentPRMergedPayload;
  'agent.pr_closed': AgentPRClosedPayload;
  'agent.pr_checks_failed': AgentPRChecksFailedPayload;
  'agent.pr_review': AgentPRReviewPayload;
  'agent.setup': AgentSetupPayload;
  'agent.tool_use': AgentToolUsePayload;
  'agent.dev_server': AgentDevServerPayload;
  'agent.performance_warning': AgentPerformanceWarningPayload;
  'agent.daemon_started': AgentDaemonStartedPayload;
  'agent.capability_requested': AgentCapabilityRequestedPayload;
  'agent.capability_resolved': AgentCapabilityResolvedPayload;
  'agent.work_assessment': AgentWorkAssessmentPayload;
  'agent.credentials_refreshed': AgentCredentialsRefreshedPayload;
  'agent.credentials_refresh_failed': AgentCredentialsRefreshFailedPayload;
  'agent.source.dashboard': AgentSourceDashboardPayload;
  'agent.source.flow': AgentSourceFlowPayload;
  'agent.flow_result': ReflexResultPayload;
  'agent.subagent': AgentSubagentPayload;
  'agent.compact': AgentCompactPayload;
  'agent.status_change': AgentStatusChangePayload;
  'agent.runtime_settings_changed': AgentRuntimeSettingsChangedPayload;
  'codex.command.result': CodexCommandResultPayload;
  'devbox.running': DevboxLifecyclePayload;
  'devbox.suspended': DevboxLifecyclePayload;
  'devbox.shutdown': DevboxLifecyclePayload;
  'devbox.failed': DevboxLifecyclePayload;
  'devbox.cleaned_up': DevboxCleanupPayload;
  'flow.started': Record<string, unknown>;
  'flow.node_started': Record<string, unknown>;
  'flow.node_completed': Record<string, unknown>;
  'flow.node_error': Record<string, unknown>;
  'flow.node_retrying': FlowNodeRetryingPayload;
  'flow.node_skipped': Record<string, unknown>;
  'flow.approval_requested': Record<string, unknown>;
  'flow.approval_responded': Record<string, unknown>;
  'flow.completed': Record<string, unknown>;
  'flow.error': Record<string, unknown>;
  'flow.cancelled': Record<string, unknown>;
  message: MessagePayload;
  webhook: unknown;
  'turn.started': TurnStartedPayload;
  'turn.message_chunk': TurnMessageChunkPayload;
  'turn.tool_call': TurnToolCallPayload;
  'turn.tool_call_update': TurnToolCallUpdatePayload;
  'turn.completed': TurnCompletedPayload;
  'turn.cancelled': TurnCancelledPayload;
  'turn.failed': TurnFailedPayload;
  'turn.resumed': TurnResumedPayload;
  'background.changed': BackgroundChangedPayload;
  // Claude CLI raw protocol events (unprefixed)
  system: TurnClaudeSystemPayload;
  user: TurnClaudeUserPayload;
  assistant: TurnClaudeAssistantPayload;
  result: TurnClaudeResultPayload;
  error: TurnClaudeErrorPayload;
  rate_limit_event: TurnClaudeRateLimitEventPayload;
  control_request: TurnClaudeControlRequestPayload;
  control_response: TurnClaudeControlResponsePayload;
  // ACP (Agent Client Protocol) events (unprefixed)
  user_message_chunk: AcpUserMessageChunkPayload;
  agent_message_chunk: AcpAgentMessageChunkPayload;
  agent_thought_chunk: AcpAgentThoughtChunkPayload;
  tool_call: AcpToolCallPayload;
  tool_call_update: AcpToolCallUpdatePayload;
  plan: AcpPlanPayload;
  available_commands_update: AcpAvailableCommandsUpdatePayload;
  current_mode_update: AcpCurrentModeUpdatePayload;
  config_option_update: AcpConfigOptionUpdatePayload;
  session_info_update: AcpSessionInfoUpdatePayload;
  usage_update: AcpUsageUpdatePayload;
  // Broker control events
  'broker.control.interrupt': BrokerControlInterruptPayload;
  'broker.control.cancel': BrokerControlCancelPayload;
  'broker.claude.control': BrokerClaudeControlPayload;
  'broker.acp.control': BrokerAcpControlPayload;
  // Codex input events
  'thread/settings/update': CodexThreadSettingsUpdatePayload;
  // Claude SDK input events
  query: UserMessage;
  controlRequest: ClientControlRequest;
  controlResponse: ClientControlResponse;
  // ACP input/response events (same event type used for both request and response)
  initialize: AcpInitializeRequest | AcpInitializeResponse;
  'session/new': AcpNewSessionRequest | AcpNewSessionResponse;
  'session/prompt': AcpPromptRequest | AcpPromptResponse;
  'session/cancel': AcpCancelNotification | Record<string, unknown>;
}

// Re-export so existing imports from this module continue to work.
export type { AxonEventType } from './event.types.js';

// --- Protocol event type constants and helpers ---

/** Claude protocol event types (unprefixed) */
export const CLAUDE_PROTOCOL_EVENTS = new Set([
  'system',
  'user',
  'assistant',
  'result',
  'error',
  'rate_limit_event',
  'control_request',
  'control_response',
] as const);

/** ACP protocol event types (unprefixed) */
export const ACP_PROTOCOL_EVENTS = new Set([
  'user_message_chunk',
  'agent_message_chunk',
  'agent_thought_chunk',
  'tool_call',
  'tool_call_update',
  'plan',
  'available_commands_update',
  'current_mode_update',
  'config_option_update',
  'session_info_update',
  'usage_update',
] as const);

/** Check if an event type is a Claude protocol event */
export function isClaudeProtocolEvent(type: string): boolean {
  return CLAUDE_PROTOCOL_EVENTS.has(
    type as typeof CLAUDE_PROTOCOL_EVENTS extends Set<infer T> ? T : never,
  );
}

/** Check if an event type is an ACP protocol event */
export function isAcpProtocolEvent(type: string): boolean {
  return ACP_PROTOCOL_EVENTS.has(
    type as typeof ACP_PROTOCOL_EVENTS extends Set<infer T> ? T : never,
  );
}

// Compile-time sync: every AxonEventMap key must be a valid AxonEventType.
// The reverse direction (AxonEventType ⊆ keyof AxonEventMap) is not enforced here
// because plugin `declare module` augmentations extend AxonEventMap with additional
// event types (e.g. agent.source.sentry_issue) that only exist when the plugin is imported.
type _MapKeysMatchEnum = [keyof AxonEventMap] extends [AxonEventType]
  ? true
  : 'AxonEventMap has keys not in AxonEventTypeSchema';
export type { _MapKeysMatchEnum as _AxonEventMapSyncCheck };

/** Resolves the payload type for a given event key (falls back to `unknown` for plugin-augmented keys not yet visible). */
type EventPayload<K extends AxonEventType> = K extends keyof AxonEventMap
  ? AxonEventMap[K]
  : unknown;

/**
 * A typed variant of AxonEvent where `type` narrows the `payload`.
 * Use the generic parameter to narrow to a specific event type, or
 * leave it defaulted for the full discriminated union.
 */
export type TypedAxonEvent<T extends AxonEventType = AxonEventType> = {
  [K in T]: Omit<AxonEvent, 'type' | 'payload'> & {
    type: K;
    payload: EventPayload<K>;
  };
}[T];

/**
 * Type-safe input for `AxonEventService.publish()`.
 * Discriminates on `type` so TypeScript enforces the correct payload shape
 * for each event type at compile time.
 */
export type TypedPublishInput<T extends AxonEventType = AxonEventType> = {
  [K in T]: { type: K; payload: EventPayload<K> };
}[T];
